import i18n from "../i18n/i18n";

const baseUrl = process.env.REACT_APP_GITO_AGENT_URL;
//const baseUrl = "https://localhost:7299";

function authHeaders(apiAccessToken) {
    return {
        Authorization: `Bearer ${apiAccessToken}`,
        "Content-Type": "application/json",
    };
}

function isAbortError(error) {
    return error?.name === "AbortError" || error?.name === "TimeoutError";
}

function isNetworkError(error) {
    const message = String(error?.message || "").toLowerCase();
    return (
        message === "failed to fetch" ||
        message.includes("networkerror") ||
        message.includes("load failed") ||
        message.includes("network request failed")
    );
}

async function readProblemDetail(response) {
    try {
        const body = await response.json();
        const detail = body?.detail || body?.title;
        if (typeof detail === "string" && detail && !detail.startsWith("[GITO]")) {
            return detail;
        }
    } catch {
        // Body is not JSON; fall back to status text.
    }
    return "";
}

function statusMessage(status) {
    if (status === 401) {
        return i18n.t("agent.notSignedIn");
    }
    if (status === 403) {
        return i18n.t("agent.noAccess");
    }
    if (status === 404) {
        return i18n.t("agent.chatNotFound");
    }
    if (status === 408 || status === 504) {
        return i18n.t("agent.timeout");
    }
    if (status >= 500) {
        return i18n.t("agent.unavailable");
    }
    return "";
}

async function agentFetch(url, options, fallback) {
    let response;
    try {
        response = await fetch(url, options);
    } catch (error) {
        if (isAbortError(error)) {
            throw error;
        }
        if (isNetworkError(error)) {
            throw new Error(i18n.t("agent.couldNotConnect"));
        }
        throw new Error(fallback);
    }

    if (!response.ok) {
        const fromApi = await readProblemDetail(response);
        throw new Error(fromApi || statusMessage(response.status) || fallback);
    }

    return response;
}

export async function createAgentSession(apiAccessToken) {
    const response = await agentFetch(
        `${baseUrl}/sessions`,
        {
            method: "POST",
            headers: authHeaders(apiAccessToken),
        },
        i18n.t("agent.couldNotCreateChat")
    );
    return response.json();
}

export async function listAgentSessions(apiAccessToken) {
    const response = await agentFetch(
        `${baseUrl}/sessions`,
        {
            headers: authHeaders(apiAccessToken),
        },
        i18n.t("agent.couldNotLoadChats")
    );
    return response.json();
}

export async function getAgentSession(apiAccessToken, sessionId) {
    const response = await agentFetch(
        `${baseUrl}/sessions/${encodeURIComponent(sessionId)}`,
        {
            headers: authHeaders(apiAccessToken),
        },
        i18n.t("agent.couldNotLoadChat")
    );
    return response.json();
}

export async function deleteAgentSession(apiAccessToken, sessionId) {
    await agentFetch(
        `${baseUrl}/sessions/${encodeURIComponent(sessionId)}`,
        {
            method: "DELETE",
            headers: authHeaders(apiAccessToken),
        },
        i18n.t("agent.couldNotDeleteChat")
    );
}

function artifactParams(sessionId, name, version) {
    return new URLSearchParams({
        session: sessionId,
        name,
        version: String(version ?? 0),
    });
}

export async function getAgentArtifactUrl(apiAccessToken, sessionId, name, version) {
    const response = await agentFetch(
        `${baseUrl}/artifact?${artifactParams(sessionId, name, version)}`,
        {
            headers: authHeaders(apiAccessToken),
        },
        i18n.t("agent.couldNotPrepareDownload")
    );
    return response.json();
}

export async function getAgentArtifactJson(apiAccessToken, sessionId, name, version) {
    const response = await agentFetch(
        `${baseUrl}/artifact/content?${artifactParams(sessionId, name, version)}`,
        {
            headers: authHeaders(apiAccessToken),
        },
        i18n.t("agent.couldNotLoadChart")
    );
    return response.json();
}

export async function streamAgentQuery({
    apiAccessToken,
    message,
    sessionId,
    onEvent,
    signal,
}) {
    const response = await agentFetch(
        `${baseUrl}/query/stream`,
        {
            method: "POST",
            headers: authHeaders(apiAccessToken),
            body: JSON.stringify({
                message,
                sessionId: sessionId || undefined,
            }),
            signal,
        },
        i18n.t("agent.couldNotSend")
    );

    if (!response.body) {
        throw new Error(i18n.t("agent.noReplyStream"));
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            buffer = flushSseBuffer(buffer, onEvent);
        }
    } catch (error) {
        if (isAbortError(error)) {
            throw error;
        }
        if (isNetworkError(error)) {
            throw new Error(i18n.t("agent.connectionLost"));
        }
        if (error instanceof Error && error.message) {
            throw error;
        }
        throw new Error(i18n.t("agent.couldNotReadReply"));
    }

    if (buffer.trim()) {
        flushSseBuffer(`${buffer}\n\n`, onEvent);
    }
}

function flushSseBuffer(buffer, onEvent) {
    const parts = buffer.split("\n\n");
    const rest = parts.pop() ?? "";

    parts.forEach((chunk) => {
        const dataLines = chunk
            .split("\n")
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trimStart());

        if (dataLines.length === 0) {
            return;
        }

        const raw = dataLines.join("");
        if (!raw || raw === "[DONE]") {
            return;
        }

        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed.error === "string" && parsed.error) {
                throw new Error(parsed.error);
            }
            onEvent(parsed);
        } catch (error) {
            if (error instanceof SyntaxError) {
                return;
            }
            throw error;
        }
    });

    return rest;
}
